import cx from 'classnames';
import {memo, useCallback, useRef} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {EditButton} from '~/common/components/EditButton/EditButton';
import {
  selectForceShowModule,
  selectIsSavingRegistries,
  selectRegistryNote,
  selectRegistryRecipient,
  selectRegistryUrls,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {
  selectRegistryName,
  selectRegistryNote as selectRegistryNoteFromRegistry,
} from '~/common/fabric/reducers/registry/selectors';
import {capitalizeFirstLetter, pluralizeWord} from '~/common/utils';
import {RegistryVendor} from '~/ViewInvitation/common/RegistryVendor';
import {useConnectEditor} from '~/ViewInvitation/hooks/useConnectEditor';
import {isPreview} from '~/ViewInvitation/utils';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {RegistryVendorLoading} from './RegistryVendorLoading/RegistryVendorLoading';

export const RegistryModule = memo((props) => {
  const {readonly, showHeader = true, inModal = false, preview = isPreview()} = props;
  const dataQaIdPrefix = inModal ? 'af-invite-toast-registry' : 'af-invite-pg-btn-registry';

  const containerRef = useRef(null);
  const editBtnRef = useRef(null);
  const isDesktop = useIsLargishAndUp();
  const registries = useSelector((state) => state.registry?.registries, shallowEqual);
  const localRegistryName = useSelector(selectRegistryRecipient);
  const localRegistryNote = useSelector(selectRegistryNote);
  const forceShowModule = useSelector(selectForceShowModule);
  const isSavingRegistries = useSelector(selectIsSavingRegistries);
  const regUrls = useSelector(selectRegistryUrls, shallowEqual);

  const storedRegistryName = useSelector(selectRegistryName);
  const storedRegistryNote = useSelector(selectRegistryNoteFromRegistry);

  const registryName = localRegistryName || storedRegistryName;
  const registryNote = localRegistryNote || storedRegistryNote;

  const giftRegistryType = useSelector(
    (state) => state.giftingOptions?.gift_registry_type ?? state.registry?.registry_type
  );
  const isWishlist = giftRegistryType === 'wishlist';
  const registryType = isWishlist ? 'wishlist' : 'registry';

  let moduleTitle = registryType === 'wishlist' ? 'Wishlist' : 'Gift Registry';
  if (registryName?.trim().length) {
    moduleTitle = `${capitalizeFirstLetter(registryName)}'s ${pluralizeWord(
      moduleTitle,
      registries.length
    )}`;
  }

  const allowTriggerEdit = useCallback((ev) => ev.target.tagName !== 'A', []);
  useConnectEditor(
    containerRef,
    editBtnRef,
    preview && !readonly && isDesktop && registries?.length > 0,
    allowTriggerEdit
  );

  if (!registries?.length && !forceShowModule && !isSavingRegistries) {
    return null;
  }

  const editable = preview && !readonly;
  return (
    <div
      className={cx('submodule-registry', {editable})}
      id="submodule-registry"
      ref={containerRef}
    >
      <EditButton data-qa-id="edit-registries" ref={editBtnRef} hide={!editable} />
      <div className="view-invitation__gifting-module__registry-wrapper">
        {showHeader && (
          <Typography variant="header6" data-qa-id="registry-module-title" semibold>
            {moduleTitle}
          </Typography>
        )}

        {registryNote && (
          <Typography variant="list1" className="view-invitation__registry-vendor__note">
            {registryNote}
          </Typography>
        )}

        <div
          className={`gift-registry__vendors view-invitation__registry-grid ${inModal ? 'scroll' : ''}`}
          data-qa-id="gift-registry__vendors"
        >
          {isSavingRegistries
            ? regUrls.map(() => (
                <RegistryVendorLoading
                  key={
                    // not using url because it can be the same url and bug the UI
                    crypto.randomUUID()
                  }
                />
              ))
            : registries?.map((registry, index) => (
                <RegistryVendor
                  type={registryType}
                  registry={registry}
                  dataQaId={`${dataQaIdPrefix}-${index + 1}`}
                  key={`${registry?.id ?? registry?.name}_${index + 1}`}
                />
              ))}
        </div>
      </div>
    </div>
  );
});

RegistryModule.displayName = 'RegistryModule';
